import { Trash, ChevronsUpDown } from "lucide-react";
import { useState, useRef, useCallback, useEffect, memo, useMemo } from 'react';
import { usePlaceholder } from '../../../hooks/usePlaceholder';
import { getPaises, getRegimenes, getProvincias } from '../../../utils/modalDataUtils';
import { UniversalSelectModal } from '../../common/UniversalSelectModal';
import { getModalConfig } from '../../../utils/modalConfigUtils';
import { OptimizedInput } from '../../common/OptimizedInput';
import { useDebouncedUpdate } from '../../../hooks/useDebouncedUpdate';
import { updatePartidaField, removePartida } from '../../../utils/editableDataUpdates';
import { useFieldHandlerFactory } from '../../../utils/fieldHandlers';

// Provincias in TARIC are 2-digit string codes ("01".."52").
// Some flows (templates / extracted data) have historically stored this as a number (1..52),
// so we normalize to a comparable string and avoid calling string methods on non-strings.
const normalizeProvinciaValue = (value) => {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'object') {
    if (value && value.codigo !== undefined && value.codigo !== null) {
      return normalizeProvinciaValue(value.codigo);
    }
    if (value && value.nombre !== undefined && value.nombre !== null) {
      return String(value.nombre).trim();
    }
    return '';
  }

  const str = String(value).trim();
  // If it's numeric (e.g. 1 or "1"), keep leading zeros by padding to 2 digits.
  if (/^\d+$/.test(str)) {
    return str.padStart(2, '0');
  }
  return str;
};

const ItemInformationComponent = ({
  declarationIndex,
  itemIndex,
  isSpanish,
  item,
  editableData,
  setEditableData,
  setIsDirty,
}) => {
  // const dispatch = useDispatch();
  const [isPaisOrigenModalOpen, setPaisOrigenModalOpen] = useState(false);
  const [isRegimenModalOpen, setRegimenModalOpen] = useState(false);
  const [isProvinciasModalOpen, setProvinciasModalOpen] = useState(false);
  const paises = useMemo(() => getPaises(), []);
  const regimenes = useMemo(() => getRegimenes(), []);
  const provincias = useMemo(() => getProvincias(), []);
  const selectedPaisOrigen = useMemo(() => paises.find(
    (p) => p.codigo === item.pais_origen || p.nombre === item.pais_origen
  ), [paises, item.pais_origen]);
  const selectedRegimen = useMemo(() => regimenes.find(
    (r) => r.codigo === item.regimen_solicitado_precedente || r.nombre === item.regimen_solicitado_precedente
  ), [regimenes, item.regimen_solicitado_precedente]);
  const selectedProvincia = useMemo(() => {
    const provinciaKey = normalizeProvinciaValue(item?.provincia_origen);
    if (!provinciaKey) return undefined;
    const provinciaKeyLower = provinciaKey.toLowerCase();

    return provincias.find((p) =>
      p.codigo === provinciaKey ||
      p.nombre === provinciaKey ||
      (p.nombre?.toLowerCase() === provinciaKeyLower)
    );
  }, [provincias, item?.provincia_origen]);

  const editableDataRef = useRef(editableData);

  useEffect(() => {
    editableDataRef.current = editableData;
  }, [editableData]);

  // debounced update callback
  const handleUpdate = useCallback((updatedData) => {
    setEditableData(updatedData);
    setIsDirty(true);
  }, [setEditableData, setIsDirty]);

  const { debouncedUpdate } = useDebouncedUpdate(handleUpdate, 300);

  // handle item field change using shared utility
  const handleItemChange = useCallback((field, value) => {
    const currentEditableData = editableDataRef.current;
    const updatedData = updatePartidaField(
      currentEditableData,
      declarationIndex,
      itemIndex,
      field,
      value
    );
    
    // only proceed if data actually changed
    if (updatedData === currentEditableData) {
      return;
    }
    
    editableDataRef.current = updatedData;
    
    // OPTIMIZED: Immediately validate field to remove error if field is now filled
    if (typeof window !== 'undefined' && window.__validateFieldImmediately) {
      window.__validateFieldImmediately(declarationIndex, field, value, itemIndex, null, null);
    }
    
    debouncedUpdate(updatedData);
  }, [declarationIndex, itemIndex, debouncedUpdate]);

  // handler for selecting a country from the modal
  const handlePaisOrigenSelect = useCallback((pais) => {
    handleItemChange('pais_origen', pais.codigo);
  }, [handleItemChange]);

  const handleRegimenSelect = useCallback((regimen) => {
    handleItemChange('regimen_solicitado_precedente', regimen.codigo);
  }, [handleItemChange]);

  const handleProvinciaSelect = useCallback((provincia) => {
    handleItemChange('provincia_origen', provincia.codigo);
  }, [handleItemChange]);
  
  const getFieldHandler = useFieldHandlerFactory(handleItemChange, {
    transformValue: (e, field) => {
      if (field === 'regimen_comunitario_nacional') {
        return e.target.value || null;
      }
      return e.target.value;
    }
  });

  // Memoize all placeholder values to prevent unnecessary re-renders
  const descripcionMercanciaPlaceholder = usePlaceholder({ isSpanish, fieldValue: item.descripcion_mercancia, type: 'enter_goods_description' });
  const codigoNcPlaceholder = usePlaceholder({ isSpanish, fieldValue: item.codigo_nc, type: 'enter_nc_code' });
  const masaBrutaPlaceholder = usePlaceholder({ isSpanish, fieldValue: item.masa_bruta, type: 'enter_gross_weight' });
  const masaNetaPlaceholder = usePlaceholder({ isSpanish, fieldValue: item.masa_neta, type: 'enter_net_weight' });
  const cantidadEurosPlaceholder = usePlaceholder({ isSpanish, fieldValue: item.cantidad_euros, type: 'enter_euro_amount' });
  const cantidadDivisasPlaceholder = usePlaceholder({ isSpanish, fieldValue: item.cantidad_divisas, type: 'enter_currency_amount' });
  const precioPlaceholder = usePlaceholder({ isSpanish, fieldValue: item.precio, type: 'enter_price' });
  const valorEstadisticoPlaceholder = usePlaceholder({ isSpanish, fieldValue: item.valor_estadistico, type: 'enter_statistical_value' });
  const adicionesPlaceholder = usePlaceholder({ isSpanish, fieldValue: item.adiciones, type: 'enter_price' });
  const regimenComunitarioNacionalPlaceholder = usePlaceholder({ isSpanish, fieldValue: item.regimen_comunitario_nacional, type: 'enter_community_national_regime' });
  const observacionesPlaceholder = usePlaceholder({ isSpanish, fieldValue: item.observaciones, type: 'enter_observations' });
  const example0_00Placeholder = usePlaceholder({ isSpanish, type: 'example_0_00' });
  const example512Placeholder = usePlaceholder({ isSpanish, type: 'example_512' });
  const example0Placeholder = usePlaceholder({ isSpanish, type: 'example_0' });

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold uppercase">
          {!isSpanish ? "Item" : "Partida"} #{itemIndex + 1}
        </h2>
        <button
          className="py-1 text-xs text-red-500 flex items-center gap-1"
          onClick={() => {
            const updatedData = removePartida(editableData, declarationIndex, itemIndex);
            if (updatedData !== editableData) {
            setEditableData(updatedData);
            setIsDirty(true);
            }
          }}
        >
          <Trash className={`size-4 stroke-red-500`} />
          {!isSpanish ? "Remove Item" : "Eliminar Partida"}
        </button>
      </div>
      <div className="form-group flex-1 ">
        <label
          htmlFor="descripcion_mercancia"
          className={`${!item.descripcion_mercancia && "text-red-600"}`}
        >
          {!isSpanish ? "Goods Description" : "Descripción de Mercancía"}
        </label>
        <OptimizedInput
          id={`descripcion_mercancia_${declarationIndex + 1}_${itemIndex}`}
          rows={3}
          value={item.descripcion_mercancia}
          placeholder={descripcionMercanciaPlaceholder}
          onChange={getFieldHandler("descripcion_mercancia")}
          className={`w-full border rounded px-2 py-1 mt-1 pr-8 ${
            !item.descripcion_mercancia && "required-input"
          }`}
          required
        />
      </div>
      <div className="grid grid-cols-3 gap-8 rounded-md mt-4 partida-container">
        <div className="form-group">
          <label
            htmlFor="codigo_nc"
            className={`${!item.codigo_nc && "text-red-600"}`}
          >
            {!isSpanish ? "NC Code" : "Código NC"}
          </label>
          <OptimizedInput
            id={`codigo_nc_${declarationIndex + 1}_${itemIndex}`}
            value={item.codigo_nc}
            placeholder={codigoNcPlaceholder}
            onChange={getFieldHandler("codigo_nc")}
            className={`w-full border rounded px-2 py-1 pr-8 ${
              !item.codigo_nc && "required-input"
            }`}
            required
          />
        </div>
        <div className="form-group">
          <label
            htmlFor="masa_bruta"
            className={`${!item.masa_bruta && "text-red-600"}`}
          >
            {!isSpanish ? "Gross Weight" : "Masa Bruta"}
          </label>
          <OptimizedInput
            id={`masa_bruta_${declarationIndex + 1}_${itemIndex}`}
            value={item.masa_bruta}
            placeholder={masaBrutaPlaceholder}
            onChange={getFieldHandler("masa_bruta")}
            className={`w-full border rounded px-2 py-1 pr-8 ${
              !item.masa_bruta && "required-input"
            }`}
            required
          />
        </div>
        <div className="form-group">
          <label
            htmlFor="masa_neta"
            className={`${!item.masa_neta && "text-red-600"}`}
          >
            {!isSpanish ? "Net Weight" : "Masa Neta"}
          </label>
          <OptimizedInput
            id={`masa_neta_${declarationIndex + 1}_${itemIndex}`}
            value={item.masa_neta}
            placeholder={masaNetaPlaceholder}
            onChange={getFieldHandler("masa_neta")}
            className={`w-full border rounded px-2 py-1 pr-8 ${
              !item.masa_neta && "required-input"
            }`}
            required
          />
        </div>
        <div className="form-group">
          <label
            htmlFor="cantidad_euros"
          >
            {!isSpanish ? "Euro Amount" : "Cantidad en Euros"}
          </label>
          <OptimizedInput
            id={`cantidad_euros_${declarationIndex + 1}_${itemIndex}`}
            value={item.cantidad_euros || ''}
            placeholder={cantidadEurosPlaceholder}
            onChange={getFieldHandler("cantidad_euros")}
            className="w-full border rounded px-2 py-1 pr-8"
          />
        </div>
        <div className="form-group">
          <label
            htmlFor="cantidad_divisas"
          >
            {!isSpanish ? "Currency Amount" : "Cantidad en Divisas"}
          </label>
          <OptimizedInput
            id={`cantidad_divisas_${declarationIndex + 1}_${itemIndex}`}
            value={item.cantidad_divisas || ''}
            placeholder={cantidadDivisasPlaceholder}
            onChange={getFieldHandler("cantidad_divisas")}
            className="w-full border rounded px-2 py-1 pr-8"
          />
        </div>
        <div className="form-group">
          <label
            htmlFor="pais_origen"
            className={`${!item.pais_origen && "text-red-600"}`}
          >
            {!isSpanish ? "Country of Origin" : "País de Origen"}
          </label>
          <div className="relative w-full">
            <button
            id={`pais_origen_${declarationIndex + 1}_${itemIndex}`}

              type="button"
              className={`w-full flex gap-2 justify-between items-center custom-dropwdown text-sm py-1 ${selectedPaisOrigen ? '' : 'opacity-75'}`}
              onClick={() => setPaisOrigenModalOpen(true)}
            >
              {selectedPaisOrigen ? (
                <span className="truncate">{selectedPaisOrigen.codigo} {selectedPaisOrigen.nombre && <span className="text-xs text-gray-400 ml-2">({selectedPaisOrigen.nombre})</span>}</span>
              ) : (
                item.pais_origen ? <span className="truncate">{item.pais_origen}</span> : <span className="truncate">{!isSpanish ? 'Select Country of Origin' : 'Seleccionar País de Origen'}</span>
              )}
              <ChevronsUpDown className="w-4 h-4" />
            </button>
            {isPaisOrigenModalOpen && (
              <UniversalSelectModal
                items={paises}
                isOpen={true}
                onClose={() => setPaisOrigenModalOpen(false)}
                onSelect={handlePaisOrigenSelect}
                selectedItem={selectedPaisOrigen}
                title={!isSpanish ? 'Select Country of Origin' : 'Seleccionar País de Origen'}
                isSpanish={isSpanish}
                {...getModalConfig('nameOnly', isSpanish)}
              />
            )}
          </div>
        </div>
        <div className="form-group">
          <label
            htmlFor="precio"
          >
            {!isSpanish ? "Price" : "Precio"}
          </label>
          <OptimizedInput
            id={`precio_${declarationIndex + 1}_${itemIndex}`}
            value={item.precio}
            placeholder={precioPlaceholder}
            onChange={getFieldHandler("precio")}
            className="w-full border rounded px-2 py-1 pr-8"
          />
        </div>
        <div className="form-group">
          <label
            htmlFor="provincia_origen"
          >
            {!isSpanish ? "Province of Origin" : "Provincia de Origen"}
          </label>
          <div className="relative w-full">
            <button
            id={`provincia_origen_${declarationIndex + 1}_${itemIndex}`}

              type="button"
              className={`w-full flex gap-2 justify-between items-center custom-dropwdown text-sm py-1 ${selectedProvincia ? '' : 'opacity-75'}`}
              onClick={() => setProvinciasModalOpen(true)}
            >
              {selectedProvincia ? (
                <span className="truncate" style={{ maxWidth: '200px', display: 'inline-block' }} title={`${selectedProvincia.codigo} - ${selectedProvincia.nombre}`}>
                  {selectedProvincia.codigo}
                </span>
              ) : (
                item.provincia_origen ? (
                  (() => {
                    // Try to find provincia by nombre (case-insensitive) or codigo to get codigo
                    const provinciaKey = normalizeProvinciaValue(item.provincia_origen);
                    const provinciaKeyLower = provinciaKey.toLowerCase();
                    const foundProvincia = provincias.find((p) =>
                      p.codigo === provinciaKey ||
                      p.nombre?.toLowerCase() === provinciaKeyLower
                    );
                    return foundProvincia ? (
                      <span className="truncate" title={`${foundProvincia.codigo} - ${foundProvincia.nombre}`}>
                        {foundProvincia.codigo}
                      </span>
                    ) : (
                      <span className="truncate">{item.provincia_origen}</span>
                    );
                  })()
                ) : (
                  <span className="truncate">{!isSpanish ? 'Select Province of Origin' : 'Seleccionar Provincia de Origen'}</span>
                )
              )}
              <ChevronsUpDown className="w-4 h-4" />
            </button>
            {isProvinciasModalOpen && (
              <UniversalSelectModal
                items={provincias}
                isOpen={true}
                onClose={() => setProvinciasModalOpen(false)}
                onSelect={handleProvinciaSelect}
                selectedItem={selectedProvincia}
                title={!isSpanish ? 'Select Province of Origin' : 'Seleccionar Provincia de Origen'}
                isSpanish={isSpanish}
                {...getModalConfig('nameCode', isSpanish)}
              />
            )}
          </div>
        </div>
        <div className="form-group">
          <label
            htmlFor="regimen_solicitado_precedente"
            className={`truncate`}
          >
            {!isSpanish ? "Previous Regime" : "Régimen Solicitado"}
          </label>
          <div className="relative w-full">
            <button
            id={`regimen_solicitado_precedente_${declarationIndex + 1}_${itemIndex}`}

              type="button"
              className={`w-full flex gap-2 justify-between items-center custom-dropwdown text-sm py-1 ${selectedRegimen ? '' : 'opacity-75'}`}
              onClick={() => setRegimenModalOpen(true)}
            >
              {selectedRegimen ? (
                <span className="truncate" style={{ maxWidth: '200px', display: 'inline-block' }} title={`${selectedRegimen.codigo} - ${selectedRegimen.nombre}`}>
                  {selectedRegimen.codigo} {selectedRegimen.nombre && <span className="text-xs text-gray-400 ml-2">({selectedRegimen.nombre})</span>}
                </span>
              ) : (
                item.regimen_solicitado_precedente ? <span className="truncate">{item.regimen_solicitado_precedente}</span> : <span className="truncate">{!isSpanish ? 'Select Previous Regime' : 'Seleccionar Régimen Solicitado'}</span>
              )}
              <ChevronsUpDown className="w-4 h-4" />
            </button>
            {isRegimenModalOpen && (
              <UniversalSelectModal
                items={regimenes}
                isOpen={true}
                onClose={() => setRegimenModalOpen(false)}
                onSelect={handleRegimenSelect}
                selectedItem={selectedRegimen}
                title={!isSpanish ? 'Select Previous Regime' : 'Seleccionar Régimen Solicitado'}
                isSpanish={isSpanish}
                {...getModalConfig('codeName', isSpanish)}
              />
            )}
          </div>
        </div>
        <div className="form-group">
          <label
            htmlFor="valor_estadistico"
          >
            {!isSpanish ? "Statistical Value" : "Valor Estadístico"}
          </label>
          <OptimizedInput
            id={`valor_estadistico_${declarationIndex + 1}_${itemIndex}`}
            value={item.valor_estadistico}
            placeholder={valorEstadisticoPlaceholder}
            onChange={getFieldHandler("valor_estadistico")}
            className="w-full border rounded px-2 py-1 pr-8"
          />
        </div>
        <div className="form-group">
          <label htmlFor={`adiciones_${declarationIndex + 1}_${itemIndex}`}>{!isSpanish ? "Additions" : "Adiciones"}</label>
          <OptimizedInput
            id={`adiciones_${declarationIndex + 1}_${itemIndex}`}
            value={item.adiciones || ''}
            maxLength={15}
            placeholder={example0_00Placeholder}
            onChange={getFieldHandler("adiciones")}
            className="w-full border rounded px-2 py-1 pr-8"
          />
        </div>
        <div className="form-group">
          <label htmlFor={`deducciones_${declarationIndex + 1}_${itemIndex}`}>{!isSpanish ? "Deductions" : "Deducciones"}</label>
          <OptimizedInput
            id={`deducciones_${declarationIndex + 1}_${itemIndex}`}
            value={item.deducciones || ''}
            maxLength={15}
            placeholder={example0_00Placeholder}
            onChange={getFieldHandler("deducciones")}
            className="w-full border rounded px-2 py-1 pr-8"
          />
        </div>
        <div className="form-group">
          <label htmlFor={`observaciones_arancelarias_${declarationIndex + 1}_${itemIndex}`}>{!isSpanish ? "Customs Remarks" : "Observaciones Arancelarias"}</label>
          <OptimizedInput
            id={`observaciones_arancelarias_${declarationIndex + 1}_${itemIndex}`}
            value={item.observaciones_arancelarias || ''}
            type="number"
            placeholder={example512Placeholder}
            onChange={getFieldHandler("observaciones_arancelarias")}
            className="w-full border rounded px-2 py-1 pr-8"
          />
        </div>
        <div className="form-group">
          <label htmlFor={`observaciones_arancelarias_2_${declarationIndex + 1}_${itemIndex}`}>{!isSpanish ? "Customs Remarks 2" : "Observaciones Arancelarias 2"}</label>
          <OptimizedInput
            id={`observaciones_arancelarias_2_${declarationIndex + 1}_${itemIndex}`}
            value={item.observaciones_arancelarias_2 || ''}
            type="number"
            placeholder={example0Placeholder}
            onChange={getFieldHandler("observaciones_arancelarias_2")}
            className="w-full border rounded px-2 py-1 pr-8"
          />
        </div>
        <div className="form-group">
          <label htmlFor={`deducciones_base_iva_${declarationIndex + 1}_${itemIndex}`}>{!isSpanish ? "VAT Deduction Base" : "Deducciones Base IVA"}</label>
          <OptimizedInput
            id={`deducciones_base_iva_${declarationIndex + 1}_${itemIndex}`}
            value={item.deducciones_base_iva || ''}
            maxLength={15}
            placeholder={example0_00Placeholder}
            onChange={getFieldHandler("deducciones_base_iva")}
            className="w-full border rounded px-2 py-1 pr-8"
          />
        </div>
        {/* Régimen Comunitario/Nacional - Missing field from PDF */}
        <div className="form-group">
          <label
            htmlFor={`regimen_comunitario_nacional_${declarationIndex + 1}_${itemIndex}`}
            className={`truncate`}
          >
            {!isSpanish ? "Community/National Regime" : "Régimen Comunitario/Nacional"}
          </label>
          <OptimizedInput
            id={`regimen_comunitario_nacional_${declarationIndex + 1}_${itemIndex}`}
            value={item.regimen_comunitario_nacional || ''}
            placeholder={regimenComunitarioNacionalPlaceholder}
            onChange={getFieldHandler("regimen_comunitario_nacional")}
            className="w-full border rounded px-2 py-1 pr-8"
          />
        </div>
      </div>
      {/* Observaciones per Partida */}
      <div className="form-group flex-1 mt-4">
        <label
          htmlFor={`observaciones_${declarationIndex + 1}_${itemIndex}`}
        >
          {!isSpanish ? "Observations (per Item)" : "Observaciones (por Partida)"}
        </label>
        <OptimizedInput
          id={`observaciones_${declarationIndex + 1}_${itemIndex}`}
          rows={3}
          value={item.observaciones || ''}
          placeholder={observacionesPlaceholder}
          onChange={getFieldHandler("observaciones")}
          className="w-full border rounded px-2 py-1 mt-1 pr-8"
        />
      </div>
    </div>
  );
};

// memoize component to prevent re-renders when props haven't changed
const areItemInformationPropsEqual = (prevProps, nextProps) => {
  if (prevProps.declarationIndex !== nextProps.declarationIndex) return false;
  if (prevProps.itemIndex !== nextProps.itemIndex) return false;
  if (prevProps.isSpanish !== nextProps.isSpanish) return false;
  if (prevProps.setEditableData !== nextProps.setEditableData) return false;
  if (prevProps.setIsDirty !== nextProps.setIsDirty) return false;

  const prevItem = prevProps.item;
  const nextItem = nextProps.item;

  if (prevItem === nextItem) return true;

  const relevantFields = [
    'codigo_nc', 'descripcion_mercancia', 'masa_bruta', 'masa_neta', 'pais_origen',
    'cantidad_euros', 'cantidad_divisas', 'precio', 'provincia_origen',
    'regimen_solicitado_precedente', 'regimen_comunitario_nacional', 'valor_estadistico',
    'adiciones', 'deducciones', 'observaciones_arancelarias', 'observaciones_arancelarias_2',
    'deducciones_base_iva', 'observaciones'
  ];
  
  for (const field of relevantFields) {
    if (prevItem?.[field] !== nextItem?.[field]) {
      return false;
    }
  }
  
  if ((prevItem?.documentos?.length || 0) !== (nextItem?.documentos?.length || 0)) return false;
  if ((prevItem?.embalajes?.length || 0) !== (nextItem?.embalajes?.length || 0)) return false;
  
  return true;
};

export const ItemInformation = memo(ItemInformationComponent, areItemInformationPropsEqual);
